import React from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import MessageContent from 'components/messages/MessageContent';
import PropTypes from 'prop-types';
import { findNextAndPrevPack } from 'helpers/subscriptionHelpers';
import config from 'Config';

const MinorDisableChangeUsage = ({ storeUrl }) => (
  <MessageContent>
    We&apos;re sorry but because you&apos;re under&nbsp;
    {config.adultYears}
    , we&apos;re unable to change your package online.
    Please visit&nbsp;
    <a href={storeUrl} rel="noopener noreferrer" target="_blank">
      your store
    </a>
    &nbsp;with the person who authorises your payments, where a colleague will
    be happy to help you.
  </MessageContent>
);

MinorDisableChangeUsage.propTypes = {
  storeUrl: PropTypes.string.isRequired,
};

const AltPayerDisableTopupsReason = ({ storeUrl }) => (
  <MessageContent>
    We&apos;re sorry but because someone else is paying for your easycare
    subscription, we&apos;re unable to accept top-up orders online. Please call
    us or visit&nbsp;
    <a href={storeUrl} rel="noopener noreferrer" target="_blank">
      your store
    </a>
    &nbsp;with the person who authorises your payments, where a colleague will
    be happy to help you.
  </MessageContent>
);

AltPayerDisableTopupsReason.propTypes = {
  storeUrl: PropTypes.string.isRequired,
};

const AltPayerDisableChangeUsage = ({ storeUrl }) => (
  <MessageContent>
    We&apos;re sorry but because someone else is paying for your easycare
    subscription, we&apos;re unable to change your package online. Please
    call us or visit&nbsp;
    <a href={storeUrl} rel="noopener noreferrer" target="_blank">
      your store
    </a>
    &nbsp;with the person who authorises your payments, where a colleague will
    be happy to help you.
  </MessageContent>
);

AltPayerDisableChangeUsage.propTypes = {
  storeUrl: PropTypes.string.isRequired,
};

const MinorDisableTopupsReason = ({ storeUrl }) => (
  <MessageContent>
    We&apos;re sorry but because you&apos;re under&nbsp;
    {config.adultYears}
    , we&apos;re unable to accept top-up orders online.
    Please visit&nbsp;
    <a href={storeUrl} rel="noopener noreferrer" target="_blank">
      your store
    </a>
    &nbsp;with the person who authorises your payments, where a colleague will
    be happy to help you.
  </MessageContent>
);

MinorDisableTopupsReason.propTypes = {
  storeUrl: PropTypes.string.isRequired,
};

const AlreadyToppedUpWithinThreeMonths = ({ storeUrl }) => (
  <MessageContent>
    We&apos;re sorry but because you have already topped up within the last
    three months, we&apos;re unable to accept top-up orders online. Please
    visit&nbsp;
    <a href={storeUrl} rel="noopener noreferrer" target="_blank">
      your store
    </a>
    , where a colleague will be happy to help you.
  </MessageContent>
);

AlreadyToppedUpWithinThreeMonths.propTypes = {
  storeUrl: PropTypes.string.isRequired,
};

const AlreadyProcessing = ({ storeUrl }) => (
  <MessageContent>
    We&apos;re sorry but because your pack is being processed, we&apos;re unable
    to change your package online. Please call us or visit&nbsp;
    <a href={storeUrl} rel="noopener noreferrer" target="_blank">
      your store
    </a>
    &nbsp;where a colleague will be happy to help you.
  </MessageContent>
);

AlreadyProcessing.propTypes = {
  storeUrl: PropTypes.string.isRequired,
};

const NonLensMail = ({ storeUrl }) => (
  <MessageContent>
    We&apos;re sorry but because you collect your lenses from store, we&apos;re
    unable to accept top-up orders online. Please visit&nbsp;
    <a href={storeUrl} rel="noopener noreferrer" target="_blank">
      your store
    </a>
    &nbsp;where a colleague will be happy to help you.
  </MessageContent>
);

NonLensMail.propTypes = {
  storeUrl: PropTypes.string.isRequired,
};

const NoPreviousPack = ({ phoneNumber }) => (
  <MessageContent>
    You are unable to Top up via self serve at this time. Please contact our
    team on&nbsp;
    <a href={`tel:${phoneNumber}`}>{phoneNumber}</a>
    .
  </MessageContent>
);

NoPreviousPack.propTypes = {
  phoneNumber: PropTypes.string.isRequired,
};

const ChangeBankDisableReason = ({ storeUrl }) => (
  <MessageContent>
    Sorry, you are unable to change your Bank Details because you have pending payments. Please call or visit&nbsp;
    <a href={storeUrl} rel="noopener noreferrer" target="_blank">
      your store
    </a>
    &nbsp;where a colleague will be happy to help you.
  </MessageContent>
);

ChangeBankDisableReason.propTypes = {
  storeUrl: PropTypes.string.isRequired,
};

export default () => {
  const account = useSelector((state) => state.account);
  const store = useSelector((state) => state.store);

  const toppedUpAmountWithinThreeMonths = useSelector(
    (state) => state.subscriptionsList.toppedUpAmountWithinThreeMonths,
  );

  const subscription = useSelector((state) => state.subscription) || {
    packs: [],
  };
  const today = moment().format('YYYY-MM-DD');

  const hasAuthorisedPayment = account?.flags?.find((p) => p === "HAS_AUTHORISED_PAYMENT") ?? false;

  const { nextPack, previousPack } = findNextAndPrevPack(
    subscription.packs,
    today,
  );
  const { left, right } = subscription && subscription.cart ? subscription.cart : {};
  const anyLens = [left, right].find((i) => !!i);
  const usage = anyLens && anyLens.usage;
  const topup3MonthsLimit = {
    Occasional: 60,
    Monthly: 3,
    'Part Time': 60,
    'Full Time': 60,
  };
  const isLastPack = nextPack === subscription.packs[subscription.packs.length - 1];
  const hasAllPaymentsPending = nextPack && !nextPack.payments.find((p) => p.status !== 'Pending');
  const isNonLensMail = !subscription.packs.find(
    (p) => p.non_lens_mail_order === 'No',
  );
  const isMinor = account
    && moment(account.date_of_birth)
      .add(config.adultYears, 'years')
      .isAfter();
  const hasAlternatePayer = account && !!account.alternate_payer;

  return {
    topupsDisableReason:
      (store && isMinor && <MinorDisableTopupsReason storeUrl={store.url} />)
      || (store && isNonLensMail && <NonLensMail storeUrl={store.url} />)
      || (store && hasAlternatePayer && (
        <AltPayerDisableTopupsReason storeUrl={store.url} />
      ))
      || (store && toppedUpAmountWithinThreeMonths >= topup3MonthsLimit[usage] && (
        <AlreadyToppedUpWithinThreeMonths storeUrl={store.url} />
      ))
      || (store && !previousPack && (
        <NoPreviousPack phoneNumber={config.customerServicePhoneNumber} />
      ))
      || null,
    changeUsageDisableReason:
      (store && isMinor && <MinorDisableChangeUsage storeUrl={store.url} />)
      || (store && hasAlternatePayer && (
        <AltPayerDisableChangeUsage storeUrl={store.url} />
      ))
      || (store && isLastPack && !hasAllPaymentsPending && (
        <AlreadyProcessing storeUrl={store.url} />
      ))
      || null,
    changeBankDisableReasons:
      (store && isMinor && <MinorDisableChangeUsage storeUrl={store.url} />)
      || (store && hasAuthorisedPayment && (
        <ChangeBankDisableReason storeUrl={store.url} />
      ))
      || null,
  };
};
